import React, { ReactNode, FunctionComponent } from 'react';
import classNames from 'classnames';
import { ColorGroups, ColorGroupAll, mapColorToClassname } from 'components/enum/global';
import Asset, { AssetName } from 'components/asset/asset';

export enum IconSize {
    XSMALL,
    SMALL,
    MEDIUM,
    LARGE,
    XLARGE,
    XXLARGE,
    JUMBO_MEDIUM,
    JUMBO_LARGE,
    STRETCH,
}

const mapIconSizeToClassname: { [key in IconSize]: string } = {
    [IconSize.XSMALL]: 'icon--size-xs',
    [IconSize.SMALL]: 'icon--size-s',
    [IconSize.MEDIUM]: 'icon--size-m',
    [IconSize.LARGE]: 'icon--size-l',
    [IconSize.XLARGE]: 'icon--size-xl',
    [IconSize.XXLARGE]: 'icon--size-xxl',
    [IconSize.JUMBO_MEDIUM]: 'icon--size-jumbo-m',
    [IconSize.JUMBO_LARGE]: 'icon--size-jumbo-l',
    [IconSize.STRETCH]: 'icon--size-stretch',
};

/**
 * undefined for fallback cases - when we don't show Asset
 */
export type IconName = AssetName | undefined;

export type IconProps = {
    color?: ColorGroupAll;
    size?: IconSize;
    src?: ReactNode;
    hasRtlReflect?: boolean;
    additionalClass?: string;
    qaRole?: string;
    name?: IconName;
    /**
     * SVG preserveAspectRatio attribute
     * @notice be very careful in the usage of this attribute, it may lead to unexpected results, so use it only if 100% needed and you're sure of what you're doing
     */
    preserveAspectRatio?: string;
};

const Icon: FunctionComponent<IconProps> = (props) => {
    const {
        size = IconSize.SMALL,
        color,
        src,
        hasRtlReflect,
        additionalClass,
        qaRole,
        name,
        preserveAspectRatio,
    } = props;

    const classnames = classNames(
        {
            icon: true,
            'icon--reflect': hasRtlReflect,
        },
        typeof size !== 'undefined' && mapIconSizeToClassname[size],
        typeof additionalClass !== 'undefined' && additionalClass,
        typeof color !== 'undefined' && mapColorToClassname[color]
    );

    return (
        <span
            className={classnames}
            role="presentation"
            data-qa-role={qaRole || 'icon'}
            data-qa-icon-name={name}
        >
            {name ? (
                <Asset name={name as AssetName} preserveAspectRatio={preserveAspectRatio} />
            ) : (
                src
            )}
        </span>
    );
};

export default Icon;

// todo: IconColor - refactor
export { ColorGroups as IconColor };
